import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash, ArrowLeft, Receipt, PencilSimple, CurrencyInr, Printer, ArrowsLeftRight, ArrowDown, ArrowUp, LockKey } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { CustomerCombo, ServiceCombo } from '@/components/InvoiceCombos';
import { AddCustomerDialog, AddServiceDialog } from '@/components/InvoiceDialogs';
import CustomerCredentialsDialog from '@/components/CustomerCredentialsDialog';
import EditCustomerDialog from '@/components/EditCustomerDialog';
import { useAuth } from '@/context/AuthContext';
import { useCan } from '@/lib/permissions';
import PaymentReceiptDialog from '@/components/PaymentReceiptDialog';
import RecurringDatesDialog from '@/components/RecurringDatesDialog';
import { printInvoice as sharedPrintInvoice } from '@/lib/printInvoice';
import CustomerLedgerDialog from '@/components/CustomerLedgerDialog';
import QuickFinanceDialog from '@/components/QuickFinanceDialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
const noSpinStyle = { MozAppearance: 'textfield' };

export default function EditInvoicePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { invoiceId } = useParams();
  // When the user lands here from a per-service ledger (or any other deep-link),
  // the source page passes `state.returnTo` so Close / Cancel / Save can route
  // them back instead of always falling to /invoices.
  const exitRoute = location.state?.returnTo || '/invoices';
  const { user } = useAuth();
  const canAssign = useCan('work_assign', 'view');
  const [customers, setCustomers] = useState([]);
  // Staff directory for the per-item Assignee dropdown (mirrors CreateInvoicePage).
  const [staffDirectory, setStaffDirectory] = useState([]);
  // Fullscreen Quick Finance popup (transfer/receipt/income/expense) state.
  const [quickFinanceMode, setQuickFinanceMode] = useState(null);
  const [services, setServices] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [items, setItems] = useState([]);
  const [focusNewRow, setFocusNewRow] = useState(false);
  // Recurring dates popup state — mirror of CreateInvoicePage. Opens when
  // a recurring service is picked for any row.
  const [recurringDialog, setRecurringDialog] = useState(null);
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentType, setPaymentType] = useState('full');
  const [amountPaidStr, setAmountPaidStr] = useState('');
  const [excessHandling, setExcessHandling] = useState('change');
  const [advanceUsed, setAdvanceUsed] = useState(0);
  const [originalAdvanceUsed, setOriginalAdvanceUsed] = useState(0);
  const [discountStr, setDiscountStr] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  // When the customer paid (defaults to invoice date, but editable). Reports
  // use this as "received date" if it differs from the invoice date.
  const [paymentDate, setPaymentDate] = useState('');
  // Today — used as the `max` attr on every date input so users can only
  // back-date (never future-date) an invoice or its payment.
  const todayIso = new Date().toISOString().split('T')[0];
  const [finAccounts, setFinAccounts] = useState([]);

  // Payment-section lock: once an invoice exists with payment info, the form's
  // payment fields are read-only. Clicking "Edit Receipt" opens the receipt
  // dialog which updates in place (no duplicates).
  const [invoiceDoc, setInvoiceDoc] = useState(null);
  const [initialReceiptNumber, setInitialReceiptNumber] = useState('');
  const [paymentLocked, setPaymentLocked] = useState(true);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  // "Pay" button on the Edit page opens the same dialog in *create* mode to
  // record an additional payment on an existing invoice (generates a new RCP).
  const [showNewPayDialog, setShowNewPayDialog] = useState(false);

  // Add new customer/service inline
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [addCustomerName, setAddCustomerName] = useState('');
  const [showAddService, setShowAddService] = useState(false);
  const [addServiceName, setAddServiceName] = useState('');
  const [addServiceForRow, setAddServiceForRow] = useState(-1);
  // Once the user hits Save Changes (or the bill already has receipts), the
  // Discount + Remarks inputs are hidden — they're invoice-composition fields,
  // not post-save controls.
  const [savedOnce, setSavedOnce] = useState(false);

  // Customer ledger summary card (mirrors CreateInvoicePage)
  const [custLedger, setCustLedger] = useState(null);
  const [showLedgerPopup, setShowLedgerPopup] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showEditCustomer, setShowEditCustomer] = useState(false);

  // Customer advance history — list of past advance_received entries shown
  // below the Due row when the customer has any unused balance.
  const [advHistory, setAdvHistory] = useState({ balance: 0, entries: [] });
  const [applyingAdv, setApplyingAdv] = useState(false);
  // Per-receipt unadjusted credit (overpayments + standalone advances) for
  // the invoice's customer. Mirrors the panel on CreateInvoicePage.
  const [unadjusted, setUnadjusted] = useState({ receipts: [], unadjusted_total: 0 });
  const [applyingReceiptId, setApplyingReceiptId] = useState('');
  // Applicant is the person the service is FOR — often the same as the
  // billed customer, but can differ (family member / delegate / applicant
  // on a govt scheme). Hydrated from `d.applicant_name` on invoice load and
  // sent back on the PUT payload so downstream pages can display it.
  const [applicantName, setApplicantName] = useState('');

  const refreshCustomers = () => api.get('/customers').then(r => setCustomers(r.data));

  const handleCustomerCreated = async (data) => {
    await refreshCustomers();
    if (data?.id) setCustomerId(data.id);
  };

  const handleServiceCreated = async (data) => {
    const r = await api.get('/services', { params: { include_archived: false } });
    setServices(r.data);
    if (addServiceForRow >= 0 && data?.id) {
      const newSvc = r.data.find(s => s.id === data.id);
      if (newSvc) selectServiceForRow(addServiceForRow, newSvc);
    }
    setAddServiceForRow(-1);
  };

  useEffect(() => {
    Promise.all([
      api.get('/customers'),
      api.get('/services', { params: { include_archived: false } }),
      api.get(`/invoices/${invoiceId}`),
      api.get('/finance/accounts'),
      api.get('/work/staff').catch(() => ({ data: [] })),
    ]).then(([c, s, inv, accs, staff]) => {
      setCustomers(c.data);
      setServices(s.data);
      setFinAccounts(accs.data);
      setStaffDirectory(staff.data || []);
      const d = inv.data;
      setCustomerId(d.customer_id);
      setInvoiceNumber(d.invoice_number);
      const invDate = (d.created_at || '').split('T')[0] || '';
      setInvoiceDate(invDate);
      // Hydrate payment_date — fall back to invoice date if not previously set.
      setPaymentDate((d.payment_date || '').split('T')[0] || invDate);
      setInvoiceDoc(d);
      setInitialReceiptNumber(d.initial_receipt_number || '');
      // Use the invoice's stored payment_mode; fall back to first CASH account if missing.
      const defaultCash = accs.data.find(a => a.type === 'cash') || accs.data.find(a => a.type === 'bank');
      setPaymentMode(d.payment_mode || (defaultCash ? defaultCash.id : ''));
      // Derive the display-level payment_type from live paid/due so invoices that
      // started life as Credit but were later paid show as "Full Paid" here.
      const derivedType = (d.balance_due ?? 0) <= 0
        ? 'full'
        : (d.amount_paid ?? 0) > 0 ? 'partial' : 'credit';
      setPaymentType(derivedType);
      // Cash portion = amount_paid - advance_used; for 'partial' display cash only
      const advUsed = d.advance_used || 0;
      setAdvanceUsed(advUsed);
      setOriginalAdvanceUsed(advUsed);
      const cashPart = Math.max((d.amount_paid || 0) - advUsed, 0);
      setAmountPaidStr(d.payment_type === 'partial' ? String(cashPart) : '');
      setDiscountStr((d.discount || 0) > 0 ? String(d.discount) : '');
      setNotes(d.notes || '');
      setApplicantName(d.applicant_name || d.customer_name || '');
      setItems(d.items.map(it => ({
        service_id: it.service_id, service_name: it.service_name,
        quantity: it.quantity, service_charge: it.service_charge,
        govt_fee: it.govt_fee, govt_fee_mode: it.govt_fee_mode || '',
        work_status: it.work_status || 'completed', total: it.total,
        remark: it.remark || '',
        followup: !!it.followup,
        followup_completed: !!it.followup_completed,
        assigned_to: it.assigned_to || '',
        assigned_status: it.assigned_status || '',
      })));
      // Track original wallet deductions so balance display reflects reversal-on-edit
      const deductions = {};
      d.items.forEach(it => {
        if (it.govt_fee > 0 && it.govt_fee_mode) {
          deductions[it.govt_fee_mode] = (deductions[it.govt_fee_mode] || 0) + it.govt_fee * it.quantity;
        }
      });
      setOriginalDeductions(deductions);
    }).catch(console.error).finally(() => setLoading(false));
  }, [invoiceId]);

  const selectedCustomer = customers.find(c => c.id === customerId);

  // Fetch customer ledger summary when customer changes
  useEffect(() => {
    if (customerId) {
      api.get(`/customers/${customerId}/ledger`).then(r => {
        const entries = r.data;
        const totalBilled = entries.reduce((s, e) => s + (e.debit || 0), 0);
        const totalReceived = entries.reduce((s, e) => s + (e.credit || 0), 0);
        const pendingDue = totalBilled - totalReceived;
        setCustLedger({ entries, totalBilled, totalReceived, pendingDue });
      }).catch(() => setCustLedger(null));
      api.get(`/customers/${customerId}/advance-history`).then(r => {
        setAdvHistory({ balance: r.data?.advance_balance || 0, entries: r.data?.entries || [] });
      }).catch(() => setAdvHistory({ balance: 0, entries: [] }));
    } else {
      setCustLedger(null);
      setAdvHistory({ balance: 0, entries: [] });
    }
  }, [customerId, invoiceDoc]);

  const handleApplyAdvance = async () => {
    if (!invoiceId) return;
    const due = invoiceDoc?.balance_due ?? 0;
    if (due <= 0) { toast.error('No due to apply against'); return; }
    const amt = Math.min(advHistory.balance, due);
    if (amt <= 0) { toast.error('No advance available'); return; }
    setApplyingAdv(true);
    try {
      const r = await api.post(`/invoices/${invoiceId}/apply-advance`, { amount: amt });
      toast.success(`Advance ${fmt(amt)} applied — receipt ${r.data.receipt_number}`);
      // Match Pay flow: leave the bill and return to the list once a payment
      // (cash or advance-funded) has been recorded.
      navigate(exitRoute);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply advance');
    } finally { setApplyingAdv(false); }
  };

  // Refresh unadjusted-receipts list whenever the open invoice or its
  // outstanding balance changes (e.g. after applying a credit row).
  useEffect(() => {
    if (!invoiceDoc?.customer_id) { setUnadjusted({ receipts: [], unadjusted_total: 0 }); return; }
    api.get(`/customers/${invoiceDoc.customer_id}/unadjusted-receipts`)
      .then(r => setUnadjusted({
        receipts: (r.data?.receipts || []).filter(x => (x.unapplied_amount || 0) > 0.01),
        unadjusted_total: r.data?.unadjusted_total || 0,
      }))
      .catch(() => setUnadjusted({ receipts: [], unadjusted_total: 0 }));
  }, [invoiceDoc?.id, invoiceDoc?.balance_due]);

  const handleApplyUnadjustedReceipt = async (rcp) => {
    const due = invoiceDoc?.balance_due ?? 0;
    if (due <= 0) { toast.error('Bill is already settled'); return; }
    const amt = Math.min(rcp.unapplied_amount || 0, due);
    if (amt <= 0) return;
    setApplyingReceiptId(rcp.txn_id);
    try {
      await api.post(`/invoices/${invoiceId}/apply-advance`, {
        amount: amt,
        source_txn_id: rcp.txn_id,
      });
      toast.success(`${fmt(amt)} from ${rcp.voucher_number || 'receipt'} applied to ${invoiceDoc?.invoice_number || ''}`);
      const inv = await api.get(`/invoices/${invoiceId}`);
      setInvoiceDoc(inv.data);
      const ur = await api.get(`/customers/${invoiceDoc.customer_id}/unadjusted-receipts`);
      setUnadjusted({
        receipts: (ur.data?.receipts || []).filter(x => (x.unapplied_amount || 0) > 0.01),
        unadjusted_total: ur.data?.unadjusted_total || 0,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply receipt');
    } finally { setApplyingReceiptId(''); }
  };

  const addEmptyItem = () => {
    // Functional update so we always append to the latest items array, even
    // when triggered from a stale keyboard-handler closure (Alt+I).
    // Fresh row starts with EMPTY work_status — `selectServiceForRow` will
    // set it to 'completed' when the service does not require follow-up,
    // and leave it blank otherwise (forcing the operator to pick a stage).
    setItems(prev => [...prev, {
      service_id: '', service_name: '', quantity: 1, service_charge: 0, govt_fee: 0,
      govt_fee_mode: '', work_status: '', total: 0, remark: '', followup: false, followup_completed: false,
      assigned_to: '',
    }]);
    setFocusNewRow(true);
  };

  // Add-Item gate — blocks adding a new row while any existing row still
  // has an empty `work_status`. Operator must pick a stage (or the
  // followup flag must be cleared, which auto-sets work_status='completed').
  const canAddItem = () => items.every(it => !!it.work_status);

  // After the new row is rendered, click its service combo trigger so the
  // dropdown opens automatically — staff just starts typing the service
  // name. Mirrors the create-invoice flow.
  useEffect(() => {
    if (focusNewRow) {
      const lastIdx = items.length - 1;
      const el = document.querySelector(`[data-testid="service-select-${lastIdx}"]`);
      if (el) el.click();
      setFocusNewRow(false);
    }
  }, [focusNewRow, items.length]);

  // Global Alt+I shortcut — appends a new item row + opens its service combo.
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && (e.key === 'i' || e.key === 'I')) {
        e.preventDefault();
        if (!canAddItem()) {
          toast.error('Pick a work status for the current item before adding a new one');
          return;
        }
        addEmptyItem();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const updateItem = (idx, field, value) => {
    setItems(items.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it, [field]: value };
      if (field === 'service_id') {
        const svc = services.find(s => s.id === value);
        if (svc) { updated.service_name = svc.name; updated.service_charge = svc.service_charge; updated.govt_fee = svc.govt_fee; }
      }
      // Followup toggle drives work_status defaulting (mirror of CreateInvoicePage).
      if (field === 'followup') {
        updated.work_status = value ? '' : 'completed';
        if (value) {
          setTimeout(() => {
            document.querySelector(`[data-testid="work-status-${idx}"]`)?.click();
          }, 60);
        }
      }
      const qty = field === 'quantity' ? (parseInt(value) || 1) : updated.quantity;
      const sc = field === 'service_charge' ? (parseFloat(value) || 0) : updated.service_charge;
      const gf = field === 'govt_fee' ? (parseFloat(value) || 0) : updated.govt_fee;
      updated.quantity = qty; updated.service_charge = sc; updated.govt_fee = gf;
      updated.total = qty * (sc + gf);

      // If govt fee becomes zero, clear the fee mode
      if (gf === 0) {
        updated.govt_fee_mode = '';
      } else if (field === 'govt_fee') {
        // When govt fee is being set/changed to a non-zero value, auto-apply the service's default fee mode
        const svc = services.find(s => s.id === updated.service_id);
        if (svc?.default_fee_mode) {
          updated.govt_fee_mode = svc.default_fee_mode;
        }
      }

      // Validate fee mode balance whenever fee/qty/mode changes
      if ((field === 'govt_fee' || field === 'quantity' || field === 'govt_fee_mode') && updated.govt_fee_mode && updated.govt_fee > 0) {
        const account = finAccounts.find(a => a.id === updated.govt_fee_mode);
        if (account) {
          let usedByOthers = 0;
          items.forEach((other, oi) => {
            if (oi !== idx && other.govt_fee_mode === updated.govt_fee_mode && other.govt_fee > 0) {
              usedByOthers += other.govt_fee * other.quantity;
            }
          });
          const restored = originalDeductions[updated.govt_fee_mode] || 0;
          const available = account.balance + restored - usedByOthers;
          const need = updated.govt_fee * updated.quantity;
          // Credit card & creditor accounts may freely swing negative.
          const skipGuard = account.type === 'credit_card' || account.type === 'creditor';
          if (!skipGuard && need > available) {
            toast.error('Insufficient Fund');
            updated.govt_fee_mode = '';
          }
        }
      }
      return updated;
    }));
  };

  const selectServiceForRow = (idx, svc) => {
    setItems(items.map((it, i) => {
      if (i !== idx) return it;
      const qty = it.quantity;
      const feeMode = svc.default_fee_mode || it.govt_fee_mode;
      const needsFollowup = !!svc.requires_followup;
      const workStatus = needsFollowup ? '' : 'completed';
      return {
        ...it,
        service_id: svc.id,
        service_name: svc.name,
        service_charge: svc.service_charge,
        govt_fee: svc.govt_fee,
        govt_fee_mode: feeMode,
        total: qty * (svc.service_charge + svc.govt_fee),
        followup: needsFollowup,
        work_status: workStatus,
        // Recurring snapshot — mirror of CreateInvoicePage. Operator-facing
        // dialog will refine `recurring_start_date` / `recurring_end_date`.
        is_recurring: !!svc.is_recurring,
        recurring_interval: svc.recurring_interval || '',
        recurring_interval_months: svc.recurring_interval_months || null,
      };
    }));
    // Mirror CreateInvoicePage: auto-open Work Status dropdown when the
    // chosen service requires follow-up, so the operator is nudged to pick
    // the in-flight stage immediately.
    if (svc.requires_followup) {
      setTimeout(() => {
        document.querySelector(`[data-testid="work-status-${idx}"]`)?.click();
      }, 80);
    }
    // For recurring services, pop the dates dialog so the operator can
    // confirm From date / view auto-filled To date.
    if (svc.is_recurring) {
      const initialFrom = (invoiceDoc?.invoice_date && invoiceDoc.invoice_date.slice(0, 10)) || new Date().toISOString().slice(0, 10);
      setRecurringDialog({ idx, service: svc, initialFrom });
    }
  };

  // Original deductions made by THIS invoice — restore so balance shows post-reversal availability
  const [originalDeductions, setOriginalDeductions] = useState({});
  const [negAccounts, setNegAccounts] = useState(null);

  // Available balance per account, accounting for other rows in the form & restoring original deductions
  const getAvailableBalance = (accountId, currentRowIdx) => {
    const account = finAccounts.find(a => a.id === accountId);
    if (!account) return 0;
    let usedByOthers = 0;
    items.forEach((item, i) => {
      if (i !== currentRowIdx && item.govt_fee_mode === accountId && item.govt_fee > 0) {
        usedByOthers += item.govt_fee * item.quantity;
      }
    });
    const restored = originalDeductions[accountId] || 0;
    return account.balance + restored - usedByOthers;
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const subtotal = items.reduce((s, it) => s + it.total, 0);
  const totalSvcCharge = items.reduce((s, it) => s + (it.service_charge * it.quantity), 0);
  const totalGovtFee = items.reduce((s, it) => s + (it.govt_fee * it.quantity), 0);
  const discount = Math.max(0, Math.min(parseFloat(discountStr) || 0, subtotal));
  const total = Math.max(0, subtotal - discount);
  const amountPaid = parseFloat(amountPaidStr) || 0;
  const effectiveAdvance = Math.min(advanceUsed || 0, total);
  const rawPaying = paymentType === 'full' ? total
                  : paymentType === 'credit' ? 0
                  : amountPaid + effectiveAdvance;
  const paying = Math.min(rawPaying, total);
  const balanceDue = total - paying;
  const cashOwedAfterAdvance = Math.max(total - effectiveAdvance, 0);
  const changeToReturn = paymentType === 'partial' ? Math.max(amountPaid - cashOwedAfterAdvance, 0) : 0;

  const handleSubmit = async () => {
    if (!customerId) { toast.error('Select a customer'); return; }
    if (!items.some(i => i.service_id)) { toast.error('Add at least one service'); return; }
    const validItems = items.filter(i => i.service_id);

    // Block save if any item has a govt fee > 0 without a fee mode selected
    const missingFeeMode = [];
    items.forEach((it, idx) => {
      if (it.service_id && (it.govt_fee || 0) > 0 && !it.govt_fee_mode) {
        missingFeeMode.push(idx + 1);
      }
    });
    if (missingFeeMode.length > 0) {
      const rows = missingFeeMode.join(', ');
      toast.error(`Please select Fee Mode for row${missingFeeMode.length > 1 ? 's' : ''} ${rows} (govt fee is non-zero).`);
      return;
    }

    // Hard-block: any fee-mode account that would go negative (taking original deduction reversal into account)
    const deductions = {};
    validItems.forEach(it => {
      if (it.govt_fee > 0 && it.govt_fee_mode) {
        deductions[it.govt_fee_mode] = (deductions[it.govt_fee_mode] || 0) + it.govt_fee * it.quantity;
      }
    });
    const negatives = [];
    Object.entries(deductions).forEach(([id, ded]) => {
      const acc = finAccounts.find(a => a.id === id);
      if (!acc) return;
      // Credit cards & creditors may freely swing negative — exclude them.
      if (acc.type === 'credit_card' || acc.type === 'creditor') return;
      const restore = originalDeductions[id] || 0;
      const available = acc.balance + restore;
      if (available < ded) negatives.push({ name: acc.name, current: available, deduction: ded });
    });
    if (negatives.length > 0) {
      setNegAccounts(negatives);
      return;
    }

    setSubmitting(true);
    try {
      const followup = validItems.some(i => i.followup);
      // Auto-recover from a 409 "Invoice number ... is already in use"
      // by re-fetching the next available auto-number and retrying the PUT
      // once. Keeps Edit UX consistent with Create — staff never sees a
      // dead-end error toast; the bill just saves under a fresh serial.
      const desiredNo = (invoiceNumber || '').trim();
      const doPut = (invNo) => api.put(`/invoices/${invoiceId}`, {
        customer_id: customerId, items: validItems,
        applicant_name: (applicantName || '').trim(),
        invoice_number: invNo,
        payment_mode: paymentMode, payment_type: paymentType, amount_paid: paying, notes, followup,
        invoice_date: invoiceDate,
        payment_date: paymentDate,
        excess_handling: excessHandling, excess_amount: changeToReturn,
        advance_used: paymentType === 'credit' ? 0 : effectiveAdvance,
        discount,
      });
      try {
        await doPut(desiredNo);
      } catch (err) {
        const detail = err?.response?.data?.detail || '';
        const isClash = err?.response?.status === 409 && /already in use/i.test(detail);
        if (!isClash) throw err;
        let nextNo = '';
        try {
          const r = await api.get('/invoices/next-number');
          nextNo = (r.data?.invoice_number || '').trim();
        } catch { /* empty falls back to server auto-sequence on retry */ }
        await doPut(nextNo);
        setInvoiceNumber(nextNo);
        toast.warning(`Invoice number ${desiredNo} was already taken — saved as ${nextNo || 'the next available number'}.`);
      }
      toast.success('Invoice updated');
      setSavedOnce(true);
      // Reload the invoice in place AND fully re-hydrate the derived form
      // state — without this the Due row, payment_type pill, and Amount
      // Paid input keep their pre-save values until the user closes the
      // page and reopens it. We mirror the initial useEffect's setter
      // sequence so the page acts as if freshly loaded.
      const inv = await api.get(`/invoices/${invoiceId}`);
      const d = inv.data;
      setInvoiceDoc(d);
      const derivedType = (d.balance_due ?? 0) <= 0
        ? 'full'
        : (d.amount_paid ?? 0) > 0 ? 'partial' : 'credit';
      setPaymentType(derivedType);
      const advUsed = d.advance_used || 0;
      setAdvanceUsed(advUsed);
      setOriginalAdvanceUsed(advUsed);
      const cashPart = Math.max((d.amount_paid || 0) - advUsed, 0);
      setAmountPaidStr(d.payment_type === 'partial' ? String(cashPart) : '');
      setDiscountStr((d.discount || 0) > 0 ? String(d.discount) : '');
      setNotes(d.notes || '');
      setApplicantName(d.applicant_name || d.customer_name || '');
      setPaymentMode(d.payment_mode || paymentMode);
      const invDate = (d.created_at || '').split('T')[0] || '';
      setInvoiceDate(invDate);
      setPaymentDate((d.payment_date || '').split('T')[0] || invDate);
      setInitialReceiptNumber(d.initial_receipt_number || '');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update invoice'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteReceipt = async (receiptNumber) => {
    if (!receiptNumber) return;
    if (!window.confirm(`Delete receipt ${receiptNumber}? Linked finance entries will be reversed and the customer's outstanding balance will be restored.`)) return;
    try {
      const r = await api.delete(`/invoices/${invoiceId}/receipts/${receiptNumber}`);
      toast.success(`Receipt ${receiptNumber} deleted`);
      const inv = await api.get(`/invoices/${invoiceId}`);
      setInvoiceDoc(inv.data);
      const adv = await api.get(`/customers/${customerId}/advance-history`);
      setAdvHistory({ balance: adv.data?.advance_balance || 0, entries: adv.data?.entries || [] });
      const cs = await api.get('/customers');
      setCustomers(cs.data);
      // also refresh ledger summary
      const l = await api.get(`/customers/${customerId}/ledger`);
      const entries = l.data;
      const totalBilled = entries.reduce((s, e) => s + (e.debit || 0), 0);
      const totalReceived = entries.reduce((s, e) => s + (e.credit || 0), 0);
      setCustLedger({ entries, totalBilled, totalReceived, pendingDue: totalBilled - totalReceived });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete receipt');
    }
  };

  const handleCancelInvoice = async () => {
    if (!invoiceId || !invoiceDoc) return;
    if (!window.confirm(`Delete invoice ${invoiceDoc.invoice_number}? This is permanent. Allowed because no payments are attached.`)) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success(`Invoice ${invoiceDoc.invoice_number} deleted`);
      navigate(exitRoute);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel invoice');
    }
  };

  // Print uses the shared `printInvoice` helper (`lib/printInvoice.js`) so
  // the preview here matches the Invoices list "Actions → Print" and the
  // Create/Post-save flow exactly — one canonical bill layout.
  const handlePrint = () => {
    if (!invoiceDoc) { toast.error('Invoice not loaded yet'); return; }
    // Build the invoice-like payload from the live edit-form state so the
    // preview reflects unsaved tweaks (line-item edits, applicant change,
    // discount, etc.) instead of only the last-persisted snapshot.
    const validItems = items.filter(i => i.service_id);
    sharedPrintInvoice({
      invoice: {
        ...invoiceDoc,
        invoice_number: invoiceNumber || invoiceDoc.invoice_number,
        customer_id: customerId,
        customer_name: customers.find(c => c.id === customerId)?.name || invoiceDoc.customer_name,
        applicant_name: applicantName,
        items: validItems,
        discount: discount || 0,
        total: invoiceDoc.total || total,
        amount_paid: invoiceDoc.amount_paid || 0,
        balance_due: invoiceDoc.balance_due || 0,
        payment_mode: paymentMode,
        created_at: invoiceDoc.created_at,
      },
      finAccounts,
      customer: customers.find(c => c.id === customerId),
    });
  };

  if (loading) return <div className="p-8 text-sm text-[#52525B]">Loading invoice...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="edit-invoice-page">
      <style>{`input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; } input[type=number] { -moz-appearance: textfield; }`}</style>

      <div className="flex items-center gap-2 mb-4 justify-between flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate(exitRoute)} className="rounded-lg" data-testid="back-btn"><ArrowLeft size={18} /></Button>
        {/* Same quick finance toolbar as the Create Invoice page. */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setQuickFinanceMode('transfer')}
            className="rounded-lg h-8 text-xs border-[#F59E0B] text-[#B45309] hover:bg-[#FEF3C7]"
            data-testid="quick-transfer-btn">
            <ArrowsLeftRight size={14} className="mr-1" /> Transfer
          </Button>
          <Button size="sm" variant="outline" onClick={() => setQuickFinanceMode('receipt')}
            className="rounded-lg h-8 text-xs border-[#10B981] text-[#10B981] hover:bg-[#ECFDF5]"
            data-testid="quick-receipt-btn">
            <Receipt size={14} className="mr-1" /> Receipt
          </Button>
          <Button size="sm" variant="outline" onClick={() => setQuickFinanceMode('income')}
            className="rounded-lg h-8 text-xs border-[#10B981] text-[#10B981] hover:bg-[#ECFDF5]"
            data-testid="quick-income-btn">
            <ArrowDown size={14} className="mr-1" /> Income
          </Button>
          <Button size="sm" variant="outline" onClick={() => setQuickFinanceMode('expense')}
            className="rounded-lg h-8 text-xs border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2]"
            data-testid="quick-expense-btn">
            <ArrowUp size={14} className="mr-1" /> Expense
          </Button>
        </div>
      </div>

      <div className="space-y-5">
        {/* Customer + Ledger Summary + Invoice#/Date (fixed-width on right) */}
        <div className="flex flex-col lg:flex-row gap-4">
          <Card className="rounded-xl border-[#E4E4E7] shadow-sm lg:basis-1/2 lg:flex-shrink-0">
            <CardHeader className="p-5 pb-2"><CardTitle className="text-base font-semibold">Customer *</CardTitle></CardHeader>
            <CardContent className="p-5 pt-2">
              {/* Customer picker + Applicant textbox side-by-side. Applicant
                  defaults to the customer's name; edit only when service is
                  being done for a different person. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Customer</Label>
                  <div className="mt-1">
                    <CustomerCombo
                      customers={[...customers].sort((a, b) => a.name.localeCompare(b.name))}
                      value={customerId}
                      onSelect={id => {
                        // Keep Applicant in sync while it's still on the
                        // previous customer's default; preserve any manual
                        // override the operator has typed.
                        const prev = customers.find(c => c.id === customerId)?.name || '';
                        const next = customers.find(c => c.id === id)?.name || '';
                        setCustomerId(id);
                        setApplicantName(a => (!a || a === prev) ? next : a);
                      }}
                      onAddNew={(q) => { setAddCustomerName(q || ''); setShowAddCustomer(true); }}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Applicant</Label>
                  <Input
                    value={applicantName}
                    onChange={e => setApplicantName(e.target.value)}
                    placeholder={selectedCustomer?.name || 'Applicant name'}
                    className="rounded-lg mt-1 h-9 text-sm"
                    data-testid="applicant-name-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ledger Summary — fills the middle space */}
          {selectedCustomer && custLedger && (
            <Card className={`rounded-xl shadow-sm flex-1 min-w-0 ${custLedger.pendingDue > 0 ? 'border-[#FCA5A5] bg-[#FEF2F2]' : 'border-[#BBF7D0] bg-[#F0FDF4]'}`} data-testid="customer-summary-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  {/* LEFT — customer details (top → bottom) + action icons */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-semibold text-[#0A0A0A] truncate" title={selectedCustomer.name}>{selectedCustomer.name}</p>
                    <p className="text-[11px] text-[#52525B] truncate font-mono mt-0.5" title={selectedCustomer.phone || ''}>
                      {selectedCustomer.phone || <span className="text-[#A1A1AA] italic font-sans">No contact</span>}
                    </p>
                    <p className="text-[11px] text-[#52525B] truncate mt-0.5" title={selectedCustomer.address || ''}>
                      {selectedCustomer.address || <span className="text-[#A1A1AA] italic">No address</span>}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-[#3B82F6] hover:text-[#2563EB] hover:bg-[#EFF6FF]"
                        onClick={() => setShowCredentials(true)}
                        title="View / edit saved login IDs"
                        data-testid="view-credentials-btn"
                      >
                        <LockKey size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-[#3B82F6] hover:text-[#2563EB] hover:bg-[#EFF6FF]"
                        onClick={() => setShowEditCustomer(true)}
                        title="Edit customer details"
                        data-testid="edit-customer-btn"
                      >
                        <PencilSimple size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-[#3B82F6] hover:text-[#2563EB]" onClick={() => setShowLedgerPopup(true)} data-testid="view-ledger-btn">
                        View Ledger
                      </Button>
                    </div>
                  </div>
                  {/* RIGHT — Billed / Received / Due stacked top-to-bottom */}
                  <div className="text-right shrink-0 space-y-1">
                    <div>
                      <span className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA] mr-2">Billed</span>
                      <span className="text-sm font-bold font-mono">{fmt(custLedger.totalBilled)}</span>
                    </div>
                    <div>
                      <span className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA] mr-2">Received</span>
                      <span className="text-sm font-bold font-mono text-[#10B981]">{fmt(custLedger.totalReceived)}</span>
                    </div>
                    <div>
                      <span className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA] mr-2">Due</span>
                      <span className={`text-sm font-bold font-mono ${custLedger.pendingDue > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{fmt(custLedger.pendingDue)}</span>
                    </div>
                  </div>
                </div>
                {(selectedCustomer.advance_balance || 0) > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#E4E4E7]/70 flex items-center gap-2">
                    <span className="text-[9px] uppercase tracking-[0.12em] text-[#A1A1AA]">Advance Held</span>
                    <span className="text-sm font-bold font-mono text-[#3B82F6]" data-testid="customer-advance-held">{fmt(selectedCustomer.advance_balance)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Invoice# + Date — fixed-width card on the right */}
          <Card className="rounded-xl border-[#E4E4E7] shadow-sm lg:w-64 lg:flex-shrink-0 lg:ml-auto" data-testid="invoice-meta-card">
            <CardContent className="p-4 h-full flex flex-col justify-center gap-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#A1A1AA]">Invoice #</p>
                <Input
                  value={invoiceNumber}
                  onChange={e => setInvoiceNumber(e.target.value)}
                  placeholder="INV-#####"
                  className="rounded-lg h-8 text-sm w-36 font-mono font-bold text-[#3B82F6] text-right border-dashed border-[#E4E4E7] hover:border-[#3B82F6] focus:border-[#3B82F6]"
                  data-testid="edit-invoice-number"
                />
              </div>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#A1A1AA]">Date</Label>
                <Input type="date" value={invoiceDate} max={todayIso} onChange={e => { setInvoiceDate(e.target.value); setPaymentDate(e.target.value); }}
                  className="rounded-lg h-8 text-sm w-36" data-testid="invoice-date-input" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <p className="text-base font-semibold mb-3">Items *</p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-[#F8FAFC] z-10">
                <tr className="border-b-2 border-[#E4E4E7]">
                  <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-left pb-2 pr-2 w-56">Item</th>
                  <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-center pb-2 px-1 w-12">Qty</th>
                  <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-right pb-2 px-1 w-[70px]">Svc Charge</th>
                  <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-right pb-2 px-1 w-[80px]">Govt Fee</th>
                  <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-center pb-2 px-1 w-28">Fee Mode</th>
                  <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-center pb-2 px-1 w-28">Work Status</th>
                  <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-right pb-2 px-1 w-20">Total</th>
                  <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-left pb-2 px-1 w-28">Remarks</th>
                  {canAssign && (
                    <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-center pb-2 px-1 w-24">Assigned To</th>
                  )}
                  <th className="text-xs uppercase tracking-[0.08em] font-semibold text-[#52525B] text-center pb-2 px-1 w-16">Follow-up</th>
                  <th className="w-8 pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} className="border-b border-[#F4F4F5]">
                    <td className="py-1.5 pr-2">
                      <ServiceCombo
                        services={[...services].sort((a, b) => a.name.localeCompare(b.name))}
                        value={it.service_id}
                        onSelect={s => selectServiceForRow(idx, s)}
                        onAddNew={(q) => { setAddServiceForRow(idx); setAddServiceName(q || ''); setShowAddService(true); }}
                        testId={`service-select-${idx}`}
                      />
                    </td>
                    <td className="py-1.5 px-1"><Input type="number" min={1} value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} style={noSpinStyle} className="w-12 text-center rounded-lg h-9 text-sm" data-testid={`qty-input-${idx}`} /></td>
                    <td className="py-1.5 px-1"><div className="relative"><span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A1A1AA]">&#8377;</span><Input type="number" value={it.service_charge} onChange={e => updateItem(idx, 'service_charge', e.target.value)} style={noSpinStyle} className="w-[70px] text-right rounded-lg h-9 text-sm pl-5 font-mono" data-testid={`svc-charge-${idx}`} /></div></td>
                    <td className="py-1.5 px-1"><div className="relative"><span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A1A1AA]">&#8377;</span><Input type="number" value={it.govt_fee} onChange={e => updateItem(idx, 'govt_fee', e.target.value)} style={noSpinStyle} className="w-[80px] text-right rounded-lg h-9 text-sm pl-5 font-mono" data-testid={`govt-fee-${idx}`} /></div></td>
                    <td className="py-1.5 px-1">
                      <Select value={it.govt_fee_mode} onValueChange={v => updateItem(idx, 'govt_fee_mode', v)} disabled={!it.govt_fee || it.govt_fee === 0}>
                        <SelectTrigger className={`rounded-lg h-9 text-sm w-28 ${(!it.govt_fee || it.govt_fee === 0) ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid={`fee-mode-${idx}`}>
                          <SelectValue>
                            {it.govt_fee_mode ? (() => {
                              const acc = finAccounts.find(a => a.id === it.govt_fee_mode);
                              return acc ? `${acc.name}` : '';
                            })() : ''}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {finAccounts.filter(a => a.type === 'cash' || a.type === 'bank' || a.type === 'wallet' || a.type === 'creditor' || (a.type === 'credit_card' && (a.balance || 0) <= 0)).map(a => {
                            const available = getAvailableBalance(a.id, idx);
                            const rowGovtFee = (it.govt_fee || 0) * (it.quantity || 1);
                            // Credit cards & creditor accounts allowed to swing further negative — never disabled.
                            const isInsufficient = a.type !== 'credit_card' && a.type !== 'creditor' && available < rowGovtFee && rowGovtFee > 0;
                            return (
                              <SelectItem key={a.id} value={a.id} disabled={isInsufficient}>
                                <span className={`flex items-center justify-between gap-2 w-full ${isInsufficient ? 'opacity-50' : ''}`}>
                                  <span>{a.name} <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wide">({(a.type || '').replace('_', ' ')})</span></span>
                                  <span className={`text-xs font-mono ${isInsufficient ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{fmt(available)}</span>
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-1.5 px-1">
                      <Select value={it.work_status} onValueChange={v => updateItem(idx, 'work_status', v)}>
                        <SelectTrigger className={`rounded-lg h-9 text-sm w-28 ${it.work_status === 'completed' ? 'text-[#10B981] border-[#10B981]/30' : it.work_status === 'in_process' ? 'text-[#3B82F6] border-[#3B82F6]/30' : 'text-[#F59E0B] border-[#F59E0B]/30'}`} data-testid={`work-status-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent><SelectItem value="completed">Submitted</SelectItem><SelectItem value="in_process">Preparing</SelectItem><SelectItem value="pending">Pending</SelectItem></SelectContent>
                      </Select>
                    </td>
                    <td className="py-1.5 px-1 text-right font-mono font-bold text-sm">{fmt(it.total)}</td>
                    <td className="py-1.5 px-1"><Input value={it.remark || ''} onChange={e => updateItem(idx, 'remark', e.target.value)} placeholder="..." className="w-28 rounded-lg h-9 text-xs" data-testid={`remark-${idx}`} /></td>
                    {canAssign && (
                      <td className="py-1.5 px-1">
                        {/* Assignee dropdown — Self / Unassigned / staff list. */}
                        <Select value={it.assigned_to || ''} onValueChange={v => updateItem(idx, 'assigned_to', v === '__unassigned__' ? '' : v)}>
                          <SelectTrigger className="rounded-lg h-9 text-xs px-2" data-testid={`assignee-${idx}`}>
                            <SelectValue placeholder="Unassigned" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="self">Self</SelectItem>
                            <SelectItem value="__unassigned__">Unassigned</SelectItem>
                            {staffDirectory.filter(s => s.id !== user?.id).map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    )}
                    <td className="py-1.5 px-1 text-center"><input type="checkbox" tabIndex={-1} checked={!!it.followup} onChange={e => updateItem(idx, 'followup', e.target.checked)} className="rounded h-4 w-4 cursor-pointer accent-[#3B82F6]" data-testid={`item-followup-${idx}`} /></td>
                    <td className="py-1.5 pl-1"><Button variant="ghost" size="sm" tabIndex={-1} className="h-7 w-7 p-0 text-[#EF4444]" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}><Trash size={14} /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex items-center mt-4 pt-4 border-t-2 border-[#E4E4E7] flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={addEmptyItem}
                disabled={!canAddItem()}
                className="rounded-lg h-9 text-sm border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="add-service-row-btn"
                title={canAddItem() ? 'Add Item (Alt+I)' : 'Pick a work status for the current item first'}
              >
                <Plus size={16} className="mr-1" /> Add Item
                <span className="ml-2 text-[10px] font-mono text-[#A1A1AA]">Alt+I</span>
              </Button>

              {/* Spacer pushes the action cluster to the right */}
              <div className="flex-1 min-w-[40px]" />

              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="rounded-lg h-9 px-5 text-sm font-semibold shadow-sm bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white hover:opacity-90 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-invoice-btn"
              >
                <Receipt size={16} className="mr-2" />
                {submitting ? 'Updating...' : 'Update Invoice'}
              </Button>

              <Button
                type="button"
                onClick={() => setShowNewPayDialog(true)}
                disabled={(invoiceDoc?.balance_due ?? 0) <= 0}
                className="rounded-lg h-9 px-5 text-sm font-semibold shadow-sm bg-gradient-to-r from-[#10B981] to-[#059669] text-white hover:opacity-90 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pay-new-receipt-btn"
              >
                <CurrencyInr size={14} className="mr-1" /> Pay
              </Button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="rounded-lg h-9 text-sm border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF]"
                data-testid="print-invoice-btn"
              >
                <Printer size={16} className="mr-1.5" /> Print
              </Button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate(exitRoute)}
                className="rounded-lg h-9 text-sm border-[#E4E4E7] text-[#52525B] hover:bg-[#F4F4F5] hover:text-[#0A0A0A]"
                data-testid="cancel-edit-btn"
              >
                Close
              </Button>

              {/* Delete Invoice — only available while NO money has been
                  received against this bill. We check both payment_history
                  (legacy partial-receipt model) and amount_paid (current
                  model) so partial receipts of any kind disable the button. */}
              {(invoiceDoc?.payment_history || []).length === 0 && (invoiceDoc?.amount_paid || 0) <= 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCancelInvoice}
                  className="rounded-lg h-9 text-sm border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white"
                  data-testid="delete-invoice-btn"
                  title="Delete this invoice (no payments received yet)"
                >
                  <Trash size={14} className="mr-1" /> Delete Invoice
                </Button>
              )}

              {/* Inline Discount + Remarks fields — hidden once the bill has
                  any receipts or has been saved this session. */}
              {!savedOnce && (invoiceDoc?.payment_history || []).length === 0 && (
                <>
                  <div className="ml-2 relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[#A1A1AA]">&#8377;</span>
                    <Input type="number" value={discountStr} onChange={e => setDiscountStr(e.target.value)}
                      placeholder="Discount" className="rounded-lg h-9 text-sm pl-6 font-mono w-24 border-[#F59E0B] focus-visible:ring-[#F59E0B]" data-testid="discount-input" />
                  </div>
                  <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Invoice remarks..."
                    className="rounded-lg h-9 text-sm w-44" data-testid="invoice-notes-input" />
                </>
              )}

              {/* Right-aligned 2-line totals block (Svc/Govt on top, Total below) */}
              <div className="ml-3 pl-3 border-l border-[#E4E4E7] flex flex-col items-end justify-center h-9 leading-tight" data-testid="action-row-summary">
                <div className="text-[11px] text-[#52525B] flex items-center gap-2">
                  <span>Svc: <span className="font-mono font-semibold">{fmt(totalSvcCharge)}</span></span>
                  <span>Govt: <span className="font-mono font-semibold">{fmt(totalGovtFee)}</span></span>
                  {discount > 0 && (
                    <span className="text-[#F59E0B]">Disc: <span className="font-mono font-semibold">−{fmt(discount)}</span></span>
                  )}
                </div>
                <div className="text-lg font-extrabold leading-none mt-0.5">Total: <span className="font-mono">{fmt(total)}</span></div>
              </div>
            </div>

            {/* Receipts list — placed BELOW the action row so the amount column
                lines up under the Total head in the summary block above. Then a
                separator and the Due row (always rendered, shows ₹0 when nil). */}
            <div className="border-t-2 border-[#E4E4E7] mt-3 pt-3" data-testid="receipts-section">
              {(invoiceDoc?.payment_history || []).length === 0 ? (
                <p className="text-xs text-[#A1A1AA] italic text-right">No receipts recorded yet.</p>
              ) : (
                <div className="space-y-1">
                  {(invoiceDoc?.payment_history || []).map((rcp, i) => {
                    const acc = finAccounts.find(a => a.id === rcp.payment_mode);
                    const dt = rcp.payment_date || rcp.date || rcp.created_at;
                    const dateStr = dt ? new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
                    const isAdv = !!rcp.from_advance;
                    // Show full cash received in one row: bill portion + any
                    // overpayment parked as customer credit on the same RCP.
                    const fullAmt = (rcp.amount || 0) + (rcp.overpay_amount || 0);
                    return (
                      <div key={rcp.receipt_number || i} className="flex items-center justify-end gap-4 text-xs text-[#52525B]" data-testid={`receipt-row-${i}`}>
                        <span className="text-[#71717A]">{dateStr}</span>
                        <span className="font-mono font-semibold text-[#10B981]">{rcp.receipt_number || '—'}</span>
                        <span className={`uppercase tracking-wider text-[10px] ${isAdv ? 'text-[#3B82F6] font-semibold' : 'text-[#52525B]'}`}>
                          {isAdv ? 'Adj. with Advance' : (acc ? `${acc.name} · ${acc.type}` : (rcp.payment_mode || '—'))}
                        </span>
                        <span className="font-mono font-bold text-sm text-[#10B981] min-w-[80px] text-right">{fmt(fullAmt)}</span>
                        <button
                          type="button"
                          onClick={() => handleDeleteReceipt(rcp.receipt_number)}
                          className="text-[#EF4444] hover:text-[#DC2626] p-1"
                          title="Delete this receipt"
                          data-testid={`delete-receipt-${rcp.receipt_number}`}
                        >
                          <Trash size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Unadjusted receipts panel — between recorded receipts and the
                Due row. Each row carries an Add button to apply that
                specific receipt's leftover credit toward the current bill. */}
            {(invoiceDoc?.balance_due ?? 0) > 0 && unadjusted.receipts.length > 0 && (
              <div className="border-t border-[#E4E4E7] mt-3 pt-3" data-testid="unadjusted-receipts-panel">
                <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#3B82F6] mb-2">
                  Available credit from earlier receipts
                </p>
                <div className="space-y-1">
                  {unadjusted.receipts.map((rcp, i) => {
                    const dt = rcp.date ? new Date(rcp.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
                    const due = invoiceDoc?.balance_due ?? 0;
                    const applyAmt = Math.min(rcp.unapplied_amount || 0, due);
                    const busy = applyingReceiptId === rcp.txn_id;
                    const appliedTo = rcp.applied_to || [];
                    return (
                      <div key={rcp.txn_id} className="flex flex-col items-end gap-0.5" data-testid={`unadjusted-row-${i}`}>
                        <div className="flex items-center justify-end gap-3 text-xs text-[#52525B]">
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleApplyUnadjustedReceipt(rcp)}
                            disabled={busy || applyAmt <= 0}
                            className="h-7 px-3 text-[10px] bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-md font-semibold disabled:opacity-50"
                            data-testid={`unadjusted-add-${rcp.txn_id}`}
                            title={`Adjust ${fmt(applyAmt)} from ${rcp.voucher_number || 'this receipt'}`}
                          >
                            <Plus size={11} className="mr-1" /> {busy ? 'Adding…' : `Add ${fmt(applyAmt)}`}
                          </Button>
                          <span className="text-[#71717A]">{dt}</span>
                          <span className="font-mono font-semibold text-[#3B82F6]">{rcp.voucher_number || '—'}</span>
                          <span className="uppercase tracking-wider text-[10px] text-[#3B82F6]">Unadjusted</span>
                          <span className="font-mono font-bold text-sm text-[#3B82F6] min-w-[80px] text-right">{fmt(rcp.unapplied_amount || 0)}</span>
                        </div>
                        {appliedTo.length > 0 && (
                          <div className="text-[10px] text-[#71717A] flex flex-wrap items-center justify-end gap-1.5" data-testid={`unadjusted-applied-to-${rcp.txn_id}`}>
                            <span className="text-[#A1A1AA]">used in</span>
                            {appliedTo.map((a, idx) => (
                              <span key={a.invoice_id} className="font-mono">
                                <span className="text-[#3B82F6]">{a.invoice_number}</span>
                                <span className="text-[#A1A1AA] ml-1">({fmt(a.amount)})</span>
                                {idx < appliedTo.length - 1 && <span className="text-[#A1A1AA]">,</span>}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="border-t-2 border-[#E4E4E7] mt-3 pt-3 flex items-center justify-end gap-3" data-testid="due-row">
              <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#52525B]">Due</span>
              <span className={`font-mono font-extrabold text-base min-w-[80px] text-right ${
                (invoiceDoc?.balance_due ?? 0) > 0 ? 'text-[#EF4444]'
                : (invoiceDoc?.balance_due ?? 0) < 0 ? 'text-[#8B5CF6]'
                : 'text-[#10B981]'
              }`}>
                {(invoiceDoc?.balance_due ?? 0) < 0
                  ? `+${fmt(Math.abs(invoiceDoc.balance_due))}`
                  : fmt(invoiceDoc?.balance_due ?? 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Edit the initial payment receipt without unlocking the whole section */}
      <PaymentReceiptDialog
        open={showReceiptDialog}
        onOpenChange={setShowReceiptDialog}
        invoice={invoiceDoc}
        receipt={invoiceDoc && initialReceiptNumber ? {
          receipt_number: initialReceiptNumber,
          amount: (invoiceDoc.amount_paid || 0) - (invoiceDoc.payment_history || []).reduce((s, p) => s + (p.amount || 0), 0),
          payment_mode: invoiceDoc.payment_mode,
          date: invoiceDoc.payment_date || invoiceDoc.created_at,
        } : null}
        accounts={finAccounts}
        onSaved={async () => {
          // Re-fetch the invoice so the page reflects the new receipt amount/mode/date
          try {
            const { data } = await api.get(`/invoices/${invoiceId}`);
            setInvoiceDoc(data);
            const invD = (data.created_at || '').split('T')[0] || '';
            setPaymentDate((data.payment_date || '').split('T')[0] || invD);
            setPaymentMode(data.payment_mode);
            setAmountPaidStr(String(data.amount_paid || ''));
            setPaymentType(data.balance_due <= 0 ? 'full' : data.amount_paid > 0 ? 'partial' : 'credit');
          } catch { /* no-op */ }
        }}
      />

      {/* New-payment popup — records an additional RCP against this invoice. */}
      <PaymentReceiptDialog
        open={showNewPayDialog}
        onOpenChange={setShowNewPayDialog}
        invoice={invoiceDoc}
        receipt={null}
        accounts={finAccounts}
        onSaved={async () => {
          // After a successful payment, leave the page and return to the list —
          // the typical CSC flow (collect payment → next customer) doesn't need
          // staff lingering on the bill.
          toast.success('Payment recorded');
          navigate(exitRoute);
        }}
      />

      <AddCustomerDialog
        open={showAddCustomer}
        onOpenChange={setShowAddCustomer}
        initialName={addCustomerName}
        onCreated={handleCustomerCreated}
      />
      <AddServiceDialog
        open={showAddService}
        onOpenChange={setShowAddService}
        initialName={addServiceName}
        finAccounts={finAccounts}
        onCreated={handleServiceCreated}
      />

      <AlertDialog open={!!negAccounts} onOpenChange={(v) => { if (!v) setNegAccounts(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#DC2626]">Insufficient Balance</AlertDialogTitle>
            <AlertDialogDescription>
              The following account{negAccounts?.length === 1 ? '' : 's'} ha{negAccounts?.length === 1 ? 's' : 've'} insufficient balance for the Govt Fee deduction. Please top up the account or revise the invoice.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-2">
            {negAccounts?.map((n, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-[#F4F4F5] pb-2">
                <p className="font-semibold">{n.name}</p>
                <div className="text-right font-mono text-xs">
                  <p>Available: <span className="text-[#10B981]">{fmt(n.current)}</span></p>
                  <p>Required: <span className="text-[#EF4444]">{fmt(n.deduction)}</span></p>
                </div>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setNegAccounts(null)} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] rounded-lg" data-testid="ok-insufficient-edit">OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Customer Ledger — uses the shared CustomerLedgerDialog with the
          same `variant="full"` config the Customers Master page uses, so
          this popup mirrors that one exactly (Quick Actions row, Receive
          Due, Refund advance, edit pencils on invoices/receipts). */}
      <CustomerLedgerDialog
        open={showLedgerPopup}
        onOpenChange={setShowLedgerPopup}
        customer={selectedCustomer}
        variant="full"
        onEditInvoice={(invId) => { setShowLedgerPopup(false); if (invId !== invoiceId) navigate(`/invoices/edit/${invId}`); }}
      />

      {/* Saved Login IDs — opens from the Lock icon on the customer summary card. */}
      <CustomerCredentialsDialog
        open={showCredentials}
        onOpenChange={setShowCredentials}
        customerId={selectedCustomer?.id}
        onSaved={refreshCustomers}
      />

      {/* Edit Customer details — opens from the Pencil icon on the customer summary card. */}
      <EditCustomerDialog
        open={showEditCustomer}
        onOpenChange={setShowEditCustomer}
        customerId={selectedCustomer?.id}
        onSaved={refreshCustomers}
      />

      {/* Fullscreen Quick Finance popup so the user can record an unrelated
          transfer/receipt/income/expense without losing the edit-invoice state. */}
      <QuickFinanceDialog
        open={!!quickFinanceMode}
        onOpenChange={(v) => { if (!v) setQuickFinanceMode(null); }}
        mode={quickFinanceMode}
      />

      {/* Recurring period popup — opens whenever a recurring service is
          picked on an item row. */}
      <RecurringDatesDialog
        open={!!recurringDialog}
        onOpenChange={(v) => { if (!v) setRecurringDialog(null); }}
        service={recurringDialog?.service}
        initialFromDate={recurringDialog?.initialFrom}
        initialToDate={recurringDialog?.initialTo}
        initialNotes={recurringDialog?.initialNotes}
        onConfirm={({ from, to, notes }) => {
          const idx = recurringDialog?.idx;
          if (idx === undefined) return;
          setItems(prev => prev.map((it, i) => i === idx ? {
            ...it,
            recurring_start_date: from,
            recurring_end_date: to,
            recurring_notes: notes || '',
          } : it));
          setRecurringDialog(null);
        }}
      />
    </div>
  );
}
